/**
 * Mock implementation for storyPresentationApi.
 *
 * The deck references the SAME widget ids as the canvas mock
 * (storyCanvasApi.mock.ts) so the client-side join against
 * useStoryCanvasApi's widgets works end-to-end in local dev: cover →
 * narrative context → KPI overview → full-width chart → half-width chart +
 * insight → closing.
 */
import type {
  PresentationDownloadFormatApi,
  PresentationDownloadResponseApi,
  PresentationPlanReadApi,
} from "./storyPresentation.types";

const PRESENTATION_LATENCY_MS = 250;

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

const MOCK_PRESENTATION_PLAN: PresentationPlanReadApi = {
  version: 1,
  slides: [
    { slide_id: "slide-cover", kind: "cover", canvas_item_id: null, title: "Drug X Launch Performance Analysis", slide_index: 0 },
    { slide_id: "slide-narrative-1", kind: "item", canvas_item_id: "widget-narrative-1", title: "Context", slide_index: 1 },
    { slide_id: "slide-kpi-1", kind: "item", canvas_item_id: "widget-kpi-1", title: "Total Prescriptions", slide_index: 2 },
    { slide_id: "slide-kpi-2", kind: "item", canvas_item_id: "widget-kpi-2", title: "Market Share", slide_index: 3 },
    { slide_id: "slide-kpi-3", kind: "item", canvas_item_id: "widget-kpi-3", title: "HCP Adoption", slide_index: 4 },
    { slide_id: "slide-chart-1", kind: "item", canvas_item_id: "widget-chart-1", title: "Drug X Launch Performance", slide_index: 5 },
    { slide_id: "slide-chart-2", kind: "item", canvas_item_id: "widget-chart-2", title: "Regional Uptake", slide_index: 6 },
    { slide_id: "slide-insight-1", kind: "item", canvas_item_id: "widget-insight-1", title: "Key Insight", slide_index: 7 },
    { slide_id: "slide-closing", kind: "closing", canvas_item_id: null, title: "Powered By MagicBI", slide_index: 8 },
  ],
};

export async function mockFetchPresentation(
  _workspaceId: string,
  _storyId: string,
): Promise<PresentationPlanReadApi> {
  await wait(PRESENTATION_LATENCY_MS);
  return MOCK_PRESENTATION_PLAN;
}

// ─── Presentation download mock ────────────────────────────────────────────
//
// Mirrors the live POST .../artifacts/presentation/download response shape.
// Unlike the live backend (which caches by content), it mints a new artifact
// id and signed URL every call; it keeps no state.

let artifactSeq = 0;

const MOCK_SIGNED_URL_TTL_MS = 20 * 60 * 1000;

export async function mockDownloadPresentation(
  _workspaceId: string,
  _storyId: string,
  format: PresentationDownloadFormatApi,
): Promise<PresentationDownloadResponseApi> {
  await wait(PRESENTATION_LATENCY_MS);

  artifactSeq += 1;
  const artifactId = `mock-presentation-artifact-${artifactSeq}`;
  const now = new Date();

  // Create a real blob so the browser can download the file locally.
  const mimeType =
    format === "pptx"
      ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
      : "application/pdf";
  const blob = new Blob([`[Mock ${format.toUpperCase()} — development only]`], { type: mimeType });
  const download_url = URL.createObjectURL(blob);

  return {
    artifact_id: artifactId,
    format,
    download_url,
    expires_at: new Date(now.getTime() + MOCK_SIGNED_URL_TTL_MS).toISOString(),
    preview_urls: [],
    file_size_bytes: null,
  };
}

// Mirrors POST .../artifacts/presentation/prerender: queues a render, no body.
export async function mockPrerenderPresentation(
  _workspaceId: string,
  _storyId: string,
): Promise<void> {
  await wait(PRESENTATION_LATENCY_MS);
}

// ─── Exported for tests ────────────────────────────────────────────────────────

export const __MOCK_PRESENTATION_FOR_TESTS__ = MOCK_PRESENTATION_PLAN;

export const __resetMockPresentationArtifactsForTests__ = (): void => {
  artifactSeq = 0;
};
